/**
 * Конфигурация функций приложения
 * Определяет какие функции доступны для премиум и бесплатных пользователей
 */

export type FeatureType = 
  | 'ai_chat'
  | 'analytics'
  | 'export'
  | 'recurring_transactions'
  | 'goals'
  | 'limits'
  | 'multiple_accounts'
  | 'custom_categories'
  | 'receipt_scanner'
  | 'voice_input'
  | 'telegram_notifications'
  | 'priority_support'
  | 'plaid_bank_linking';

export interface FeatureConfig {
  id: FeatureType;
  name: string;
  description: string;
  isPremium: boolean;
  icon?: string;
  // Для бесплатных пользователей можно задать лимиты
  freeLimit?: number;
  limitDescription?: string;
}

/**
 * Конфигурация всех функций приложения
 */
export const FEATURES: Record<FeatureType, FeatureConfig> = {
  ai_chat: {
    id: 'ai_chat',
    name: 'AI Чат-помощник',
    description: 'Умный ассистент для управления финансами',
    isPremium: true,
    icon: 'brain',
  },
  analytics: {
    id: 'analytics',
    name: 'Аналитика',
    description: 'Детальные графики и отчёты по финансам',
    isPremium: true, // Premium функция
    icon: 'bar-chart',
  },
  export: {
    id: 'export',
    name: 'Экспорт данных',
    description: 'Выгрузка в Excel, CSV, PDF',
    isPremium: true,
    icon: 'download',
  },
  recurring_transactions: {
    id: 'recurring_transactions',
    name: 'Регулярные транзакции',
    description: 'Автоматическое добавление повторяющихся платежей',
    isPremium: true, // Premium функция
    icon: 'repeat',
  },
  goals: {
    id: 'goals',
    name: 'Финансовые цели',
    description: 'Создание и отслеживание целей накопления',
    isPremium: true, // Premium функция
    icon: 'target',
  },
  limits: {
    id: 'limits',
    name: 'Лимиты расходов',
    description: 'Контроль бюджета по категориям',
    isPremium: false,
    freeLimit: 3, // 3 лимита
    limitDescription: 'Бесплатно: до 3 лимитов',
  },
  multiple_accounts: {
    id: 'multiple_accounts',
    name: 'Несколько счетов',
    description: 'Управление несколькими счетами и картами',
    isPremium: true,
    icon: 'wallet',
  },
  custom_categories: {
    id: 'custom_categories',
    name: 'Кастомные категории',
    description: 'Создание собственных категорий транзакций',
    isPremium: false,
    freeLimit: 5,
    limitDescription: 'Бесплатно: до 5 кастомных категорий',
  },
  receipt_scanner: {
    id: 'receipt_scanner',
    name: 'Сканер чеков',
    description: 'Автоматическое распознавание чеков',
    isPremium: true,
    icon: 'camera',
  },
  voice_input: {
    id: 'voice_input',
    name: 'Голосовой ввод',
    description: 'Добавление транзакций голосом',
    isPremium: true,
    icon: 'mic',
  },
  telegram_notifications: {
    id: 'telegram_notifications',
    name: 'Уведомления в Telegram',
    description: 'Напоминания о лимитах и целях',
    isPremium: false,
  },
  priority_support: {
    id: 'priority_support',
    name: 'Приоритетная поддержка',
    description: 'Быстрые ответы на вопросы',
    isPremium: true,
    icon: 'headphones',
  },
  plaid_bank_linking: {
    id: 'plaid_bank_linking',
    name: 'Привязка банковских счетов',
    description: 'Автоматическая синхронизация транзакций из банков',
    isPremium: true,
    icon: 'building',
  },
};

/**
 * Проверка доступа к функции
 */
export const canAccessFeature = (
  featureId: FeatureType,
  isPremiumUser: boolean
): boolean => {
  const feature = FEATURES[featureId];
  if (!feature) return false;
  
  // Премиум пользователи имеют доступ ко всему
  if (isPremiumUser) return true;
  
  // Если функция не требует премиум - доступна
  return !feature.isPremium;
};

/**
 * Проверка лимита для бесплатных пользователей
 */
export const checkFeatureLimit = (
  featureId: FeatureType,
  currentUsage: number,
  isPremiumUser: boolean
): { allowed: boolean; limit?: number; remaining?: number } => {
  const feature = FEATURES[featureId];
  
  // Премиум без ограничений
  if (isPremiumUser) {
    return { allowed: true };
  }
  
  // Если нет лимита - доступно
  if (!feature.freeLimit) {
    return { allowed: true };
  }
  
  // Проверяем лимит
  const remaining = feature.freeLimit - currentUsage;
  return {
    allowed: remaining > 0,
    limit: feature.freeLimit,
    remaining: Math.max(0, remaining),
  };
};

/**
 * Получить список всех премиум функций
 */
export const getPremiumFeatures = (): FeatureConfig[] => {
  return Object.values(FEATURES).filter(f => f.isPremium);
};

/**
 * Получить список всех бесплатных функций
 */
export const getFreeFeatures = (): FeatureConfig[] => {
  return Object.values(FEATURES).filter(f => !f.isPremium);
};

